import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import { getSupportCommandStatus } from 'apis/supportAPIs';

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 480,
    maxWidth: '100vw',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  content: {
    padding: theme.spacing(2),
    flex: 1,
    overflow: 'auto',
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
    gap: theme.spacing(2),
  },
  previewImage: {
    maxWidth: '100%',
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
  },
  jsonViewer: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: 8,
    padding: theme.spacing(2),
    overflow: 'auto',
    maxHeight: 400,
    fontFamily: 'monospace',
    fontSize: 13,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginTop: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  timestamp: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
}));

const TERMINAL_STATUSES = ['done', 'error', 'expired', 'rateLimited'];
const POLL_INTERVAL = 1000;
const OFFLINE_TIMEOUT = 60000;

const CommandStatusDrawer = ({
  open,
  onClose,
  commandId,
  commandType,
  restaurantId,
  deviceId,
  onRetry,
}) => {
  const classes = useStyles();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [offlineWarning, setOfflineWarning] = useState(false);
  const pollRef = useRef(null);
  const startTimeRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    if (!commandId) return;
    try {
      const data = await getSupportCommandStatus(commandId, { restaurantId, deviceId });
      setStatus(data);
      setError(null);

      if (TERMINAL_STATUSES.includes(data.status)) {
        stopPolling();
      }

      if (
        data.status === 'pending' &&
        startTimeRef.current &&
        Date.now() - startTimeRef.current >= OFFLINE_TIMEOUT
      ) {
        setOfflineWarning(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch status');
    }
  }, [commandId, restaurantId, deviceId, stopPolling]);

  useEffect(() => {
    if (open && commandId) {
      startTimeRef.current = Date.now();
      setOfflineWarning(false);
      setStatus(null);
      setError(null);
      poll();
      pollRef.current = setInterval(poll, POLL_INTERVAL);
    }
    return stopPolling;
  }, [open, commandId, poll, stopPolling]);

  const handleDownload = () => {
    if (status?.result?.artifactUrl) {
      const link = document.createElement('a');
      link.href = status.result.artifactUrl;
      link.download = '';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCopy = () => {
    if (status?.result?.data) {
      navigator.clipboard.writeText(
        typeof status.result.data === 'string'
          ? status.result.data
          : JSON.stringify(status.result.data, null, 2)
      );
    }
  };

  const statusChipColor = (() => {
    switch (status?.status) {
      case 'done':
        return 'success';
      case 'error':
        return 'error';
      case 'expired':
      case 'rateLimited':
        return 'warning';
      case 'running':
        return 'info';
      case 'pending':
      default:
        return 'default';
    }
  })();

  const resolvedType = status?.type || commandType;
  const resolvedParams = status?.params;

  const renderMetadata = () => (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        mb: 2,
        bgcolor: 'background.default',
      }}
      role="region"
      aria-label="Command metadata"
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
        <Typography variant="subtitle2">{resolvedType || 'Command'}</Typography>
        <Chip
          label={status?.status || 'sending'}
          color={statusChipColor}
          size="small"
          aria-label={`Status ${status?.status || 'sending'}`}
        />
      </Box>
      {commandId && (
        <Typography variant="caption" color="textSecondary" display="block" sx={{ wordBreak: 'break-all' }}>
          ID: {commandId}
        </Typography>
      )}
      {resolvedParams && Object.keys(resolvedParams).length > 0 && (
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary">
            Params
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              mt: 0.5,
              p: 1,
              bgcolor: 'grey.100',
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(resolvedParams, null, 2)}
          </Box>
        </Box>
      )}
    </Box>
  );

  const renderStatusIcon = () => {
    if (!status) return <CircularProgress size={48} />;

    switch (status.status) {
      case 'pending':
        return <CircularProgress size={48} />;
      case 'running':
        return <CircularProgress size={48} color="secondary" />;
      case 'done':
        return <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />;
      case 'expired':
      case 'rateLimited':
        return <WarningAmberIcon sx={{ fontSize: 48, color: 'warning.main' }} />;
      default:
        return <CircularProgress size={48} />;
    }
  };

  const renderStatusMessage = () => {
    if (!status) return 'Sending command…';

    switch (status.status) {
      case 'pending':
        return 'Waiting for device to accept…';
      case 'running':
        return 'Device is working…';
      case 'done':
        return status.result?.summary || 'Command completed successfully.';
      case 'error':
        return null;
      case 'expired':
        return 'Device did not pick up command in time.';
      case 'rateLimited':
        return 'Too many commands. Try again in a minute.';
      default:
        return 'Processing…';
    }
  };

  const renderPreview = () => {
    if (status?.status !== 'done') return null;

    if (commandType === 'captureScreenshot' && status.result?.artifactUrl) {
      return (
        <img
          src={status.result.artifactUrl}
          alt="Device screenshot"
          className={classes.previewImage}
        />
      );
    }

    if (
      (commandType === 'exportPrinterSettings' || commandType === 'fetchTransactions') &&
      status.result?.data
    ) {
      const formatted =
        typeof status.result.data === 'string'
          ? status.result.data
          : JSON.stringify(status.result.data, null, 2);
      return (
        <div className={classes.jsonViewer} role="region" aria-label="Command result data">
          {formatted}
        </div>
      );
    }

    return null;
  };

  const renderActions = () => {
    if (!status) return null;

    if (status.status === 'done') {
      return (
        <div className={classes.actions}>
          {status.result?.artifactUrl && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              aria-label="Download artifact"
            >
              Download
            </Button>
          )}
          {(commandType === 'captureScreenshot' || commandType === 'exportPrinterSettings') &&
            status.result?.artifactUrl && (
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                href={status.result.artifactUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Preview artifact"
              >
                Preview
              </Button>
            )}
          {commandType === 'exportPrinterSettings' && status.result?.data && (
            <Button variant="outlined" onClick={handleCopy} aria-label="Copy to clipboard">
              Copy
            </Button>
          )}
        </div>
      );
    }

    if (status.status === 'error' && onRetry) {
      return (
        <div className={classes.actions}>
          <Button
            variant="contained"
            color="error"
            startIcon={<ReplayIcon />}
            onClick={onRetry}
            aria-label="Retry command"
          >
            Retry
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.drawer, role: 'dialog', 'aria-label': 'Command status' }}
    >
      <div className={classes.header}>
        <Typography variant="h6">Command Status</Typography>
        <IconButton onClick={onClose} aria-label="Close drawer" size="small">
          <CloseIcon />
        </IconButton>
      </div>
      <Divider />
      <div className={classes.content}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderMetadata()}

        <div className={classes.statusContainer}>
          {renderStatusIcon()}
          <Typography variant="body1" align="center">
            {renderStatusMessage()}
          </Typography>
          {status?.status === 'running' && status?.acceptedAt && (
            <Typography className={classes.timestamp}>
              Accepted at: {new Date(status.acceptedAt).toLocaleTimeString()}
            </Typography>
          )}
          {status?.status === 'done' && (
            <Chip label="Completed" color="success" size="small" />
          )}
        </div>

        {status?.status === 'error' && status?.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              {status.error.code || 'Error'}
            </Typography>
            <Typography variant="body2">{status.error.message}</Typography>
          </Alert>
        )}

        {status?.status === 'expired' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            The device may be offline. The command was not picked up within the timeout window.
          </Alert>
        )}

        {status?.status === 'rateLimited' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Rate limit reached. Please wait before sending another command.
          </Alert>
        )}

        {offlineWarning && status?.status === 'pending' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Device appears offline. You may close this drawer and try again later.
          </Alert>
        )}

        {renderPreview()}
        {renderActions()}
      </div>
    </Drawer>
  );
};

CommandStatusDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  commandId: PropTypes.string,
  commandType: PropTypes.oneOf([
    'fetchTransactions',
    'captureScreenshot',
    'exportPrinterSettings',
    'importMenu',
  ]),
  restaurantId: PropTypes.string,
  deviceId: PropTypes.string,
  onRetry: PropTypes.func,
};

export default CommandStatusDrawer;
